import { Paragraph, Separator, useEvent, View, YStack } from 'tamagui'
import { useMediaQuery } from 'react-responsive'
import { Button } from '@/src/common/components/Button'
import * as E2E from '../e2e'
import * as Stores from '../stores'
import { useState } from 'react'
import { UpdateAmountDialog } from './UpdateAmountDialog'
import { MortgageUploadPending } from './MortgageUploadPending'
import { MortgageUploadRequired } from './MortgageUploadRequired'
import { MortgageVerified } from './MortgageVerified'
import { MortgageUploadRejected } from './MortgageUploadRejected'

export function ProgramEnrolled() {
  const { data: awardQueryResult } = Stores.MortgageEarnApi.endpoints[
    'GET /mortgage/earn'
  ].useQuery({})
  const [updateAmount] = Stores.MortgageEarnApi.endpoints['POST /mortgage/earn'].useMutation()

  const isLargeScreen = useMediaQuery({ minWidth: 768 })
  const [isUpdateAmountDialogOpen, setIsUpdateAmountDialogOpen] = useState(false)
  const { latestMonthlyAward, activeMonthlyAward } = awardQueryResult ?? {}
  const { awardAmountInPoints: latestAwardAmount } = latestMonthlyAward ?? {}
  const { awardAmountInPoints: activeAwardAmount } = activeMonthlyAward ?? {}

  const handleUpdateMonthlyHousingPayment = useEvent((amount: number) => {
    void updateAmount({
      body: {
        monthlyAward: amount,
      },
    })
  })

  const monthlyHousingPayment = latestAwardAmount ?? 0
  const isCardAppSourced = latestMonthlyAward?.source === 'CARD_APP'
  const doesRequireUpload = latestMonthlyAward?.status === 'UPLOAD_REQUIRED'
  const isVerificationPending = latestMonthlyAward?.status === 'PENDING'
  const isVerificationAccepted =
    latestMonthlyAward?.status === 'ACCEPTED' && latestMonthlyAward.awardAmountInPoints > 0
  const isVerificationRejected = latestMonthlyAward?.status === 'REJECTED'
  const showSeparator =
    doesRequireUpload || isVerificationAccepted || isVerificationPending || isVerificationRejected

  return (
    <View flex={1} flexDirection={isLargeScreen ? 'row' : 'column'} gap={16}>
      <YStack gap={16} flex={1} minW={0} flexBasis={isLargeScreen ? 0 : 'auto'}>
        {activeAwardAmount !== undefined ? (
          <View display="flex" flexDirection="row" justify="space-between">
            <Paragraph opacity={0.5}>Active award amount</Paragraph>
            <Paragraph>{activeAwardAmount.toLocaleString()}</Paragraph>
          </View>
        ) : null}
        {latestAwardAmount && latestAwardAmount !== activeAwardAmount ? (
          <View display="flex" flexDirection="row" justify="space-between">
            <Paragraph opacity={0.5}>Pending award amount</Paragraph>
            <Paragraph>{latestAwardAmount.toLocaleString()}</Paragraph>
          </View>
        ) : null}
        {isCardAppSourced ? (
          <Paragraph testID={E2E.Config.Ids.Text.IsCardAppAmount}>
            The mortgage amount you entered in your card application is being used to determine your
            rewards.
          </Paragraph>
        ) : null}
        <Paragraph>
          If your payment amount has changed, please update it using the “Update Mortgage Amount”
          button.
        </Paragraph>
        <View mt={16}>
          <Button
            title="Update Mortgage Amount"
            variant="text"
            onPress={() => setIsUpdateAmountDialogOpen(true)}
            segmentAction="MesaHome MortgagePayment OpenUpdateDialog"
            segmentComponent="Button"
            testID={E2E.Config.Ids.Press.UpdateMortgageAmount}
          />
          <UpdateAmountDialog
            isOpen={isUpdateAmountDialogOpen}
            onOpenChange={setIsUpdateAmountDialogOpen}
            onUpdate={handleUpdateMonthlyHousingPayment}
            initialValue={monthlyHousingPayment}
          />
        </View>
      </YStack>
      {showSeparator ? <Separator self="stretch" vertical={isLargeScreen} /> : null}
      <YStack gap={16} flex={1} minW={0} flexBasis={isLargeScreen ? 0 : 'auto'}>
        {doesRequireUpload ? <MortgageUploadRequired /> : null}
        {isVerificationPending ? (
          <MortgageUploadPending isUpdate={activeAwardAmount !== latestAwardAmount} />
        ) : null}
        {isVerificationAccepted ? <MortgageVerified /> : null}
        {isVerificationRejected ? (
          <MortgageUploadRejected
            requestedAmount={monthlyHousingPayment.toString()}
            activeAmount={activeAwardAmount?.toString()}
          />
        ) : null}
      </YStack>
    </View>
  )
}
